import { FormControl } from '@angular/forms';
import { FormControlModel } from '@thehubz/client/core';
import { CreateAssetDto, UserAgencyModel } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, MULTI_VALUE } from '@thehubz/shared/model';
import { AssetCommonService } from '../../services/asset-common.service';
/**
 * Returns a new object containing only the properties from the input object that are non-empty.
 * - For array properties, only includes arrays with at least one non-empty item.
 * - For object properties, only includes if the value is not an empty string.
 * - Skips properties with empty string values or empty arrays.
 * @param obj The input object to filter.
 * @returns A new object with only non-empty properties, or undefined if all properties are empty.
 */
export function filterNonEmptyProperties(obj: AnyType): AnyType | undefined {
  const result: AnyType = {};

  for (const key in obj) {
    const value = obj[key];

    if (Array.isArray(value)) {
      const filteredArray = value.filter((item) => (typeof item === 'object' && item !== null ? Object.values(item).some((v) => v !== '') : item !== ''));
      if (filteredArray.length > 0) {
        result[key] = filteredArray;
      }
    } else if (value !== '') {
      result[key] = value;
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

export function processNonStandardBuyoutRights(rawFormValues: AnyType): void {
  rawFormValues.usageRights = rawFormValues.usageRightsGeneralInformation.usageRights;
  rawFormValues.usageRights.forEach((usageRight: AnyType) => {
    if (usageRight.buyoutRightsTitle && usageRight.buyoutRightsTitle._id) {
      usageRight.buyoutRightsTitle = usageRight.buyoutRightsTitle._id;
    }
  });
}

/**
 * Construct form value for asset creation
 * @param rawFormValues
 * @param customFormValues
 * @returns
 */
export function constructFormValues(rawFormValues: AnyType, customFormValues: AnyType): CreateAssetDto {
  return {
    ...filterNonEmptyProperties(rawFormValues.generalInformation),
    ...filterNonEmptyProperties(rawFormValues.tags),
    ...filterNonEmptyProperties(rawFormValues.usageRight),
    makeBuyoutRights: rawFormValues.makeBuyoutRights || [],
    customFields: customFormValues,
    usageRights: rawFormValues.usageRights || [],
  };
}

export function getCustomClientClientBrand(agency: AnyType, brandingKey: CustomClients) {
  const client = agency.clients?.find((client: AnyType) => client.name.toLowerCase() === brandingKey);
  if (!client) {
    throw new Error(`Client not found for ${brandingKey} on agency`, agency);
  }

  const brand = client.brands?.find((brand: AnyType) => brand.name.toLowerCase() === brandingKey);
  if (!brand) {
    throw new Error(`Brand not found for ${brandingKey} on client`, client);
  }

  return { client, brand };
}

/**
 * Handle VKB customer asset metadata
 * @param rawFormValues
 * @returns
 */
export function handleVKBBranding(rawFormValues: AnyType, assetCommonService: AssetCommonService) {
  const generalInformationKeysToExclude = ['agency', 'client', 'brand', 'status', 'name'];
  const tagsKeysToExclude: string[] = [AssetMetadata.General.TAGS];
  const usageRightKeysToExclude: string[] = ['expiryDate', 'noExpiry'];
  return {
    ...assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.tags, tagsKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.usageRight, usageRightKeysToExclude),
  };
}

/**
 * Handle VKB customer asset metadata
 * @param rawFormValues
 * @returns
 */
export function handleSeatBranding(rawFormValues: AnyType, assetCommonService: AssetCommonService) {
  const generalInformationKeysToExclude = ['status', 'name', 'domains'];
  const tagsKeysToExclude: string[] = [AssetMetadata.General.TAGS];
  const usageRightKeysToExclude: string[] = ['expiryDate'];
  if (rawFormValues.generalInformation?.domains) {
    rawFormValues.generalInformation.domains = assetCommonService.transformVorwerkDomains(rawFormValues.generalInformation.domains);
  }
  return {
    ...assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.contentDescription, []),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.tags, tagsKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.usageRight, usageRightKeysToExclude),
  };
}

/**
 * Handle Lufthansa customer custom asset metadata
 * @param rawFormValues
 * @returns
 */
export function handleLufthansaBranding(rawFormValues: AnyType, assetCommonService: AssetCommonService) {
  const generalInformationKeysToExclude = ['agency', 'client', 'brand', 'status', 'name', 'publishStartDate', 'publishEndDate'];
  return {
    ...assetCommonService.transformAndFilterKeys(filterNonEmptyProperties(rawFormValues.generalInformation), generalInformationKeysToExclude),
  };
}

export function handleStandardBranding(rawFormValues: AnyType, assetCommonService: AssetCommonService) {
  const tagsKeysToExclude: string[] = [AssetMetadata.General.TAGS];
  const generalInformationKeysToExclude = [
    'agency',
    'client',
    'brand',
    'status',
    'name',
    'publishStartDate',
    'publishEndDate',
    'description',
    'plannedDeletionToggle',
    'plannedDeletion',
    'jobNumber',
  ];

  const productionDetailsData = assetCommonService.transformAndFilterKeys(rawFormValues.productionDetails, []);
  const productionDetailsFormData = { productionDetails: filterNonEmptyProperties(productionDetailsData) };
  return {
    ...assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(filterNonEmptyProperties(rawFormValues.tags), tagsKeysToExclude),
    ...productionDetailsFormData,
  };
}

/**
 * Handle Arval customer asset metadata
 * @param rawFormValues
 * @returns
 */
export function handleArvalBranding(rawFormValues: AnyType, brandingKey: CustomClients, assetCommonService: AssetCommonService) {
  const { client: arvalClient, brand: arvalBrand } = getCustomClientClientBrand(rawFormValues.generalInformation.agency, brandingKey);
  rawFormValues.generalInformation.client = arvalClient;
  rawFormValues.generalInformation.brand = arvalBrand;
  const tagsKeysToExclude = [AssetMetadata.General.TAGS];
  const usageRightKeysToExclude = ['attachments', 'expiryDate', 'noExpiry', 'notes', 'owner'];
  return {
    ...assetCommonService.transformAndFilterKeys(filterNonEmptyProperties(rawFormValues.tags), tagsKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.usageRight, usageRightKeysToExclude),
  };
}
/**
 * Handle Vorwerk customer asset metadata
 * @param rawFormValues
 * @returns
 */
export function handleVorwerkBranding(rawFormValues: AnyType, assetCommonService: AssetCommonService) {
  const tagsKeysToExclude = [AssetMetadata.General.TAGS];
  const generalInformationKeysToExclude = [
    'agency',
    'client',
    'brand',
    'domains',
    'status',
    'name',
    'description',
    'plannedDeletionToggle',
    'plannedDeletion',
    'publishStartDate',
    'publishEndDate',
  ];
  if (rawFormValues.generalInformation?.domains) {
    rawFormValues.generalInformation.domains = assetCommonService.transformVorwerkDomains(rawFormValues.generalInformation.domains);
  }
  return {
    ...assetCommonService.transformAndFilterKeys(filterNonEmptyProperties(rawFormValues.tags), tagsKeysToExclude),
    ...assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
  };
}

export function processStandardBuyoutRights(assetUploadBuyoutRights: AnyType[], assetCommonService: AssetCommonService): Record<string, AnyType>[] {
  const buyoutRights: Record<string, AnyType>[] = [];
  for (const usageRight of assetUploadBuyoutRights) {
    const formValueObject = { usageRightsGeneralInformation: usageRight };

    if (formValueObject.usageRightsGeneralInformation?.buyoutRightsSecondLevelType) {
      handleBuyoutRights(formValueObject);
    }

    const makeBuyoutRights = assetCommonService.constructMakeOSUsageRights(formValueObject);
    buyoutRights.push(...(makeBuyoutRights?.makeBuyoutRights || []));
  }
  return buyoutRights;
}

export function handleBuyoutRights(formValueObject: AnyType): void {
  Object.entries(formValueObject.usageRightsGeneralInformation.buyoutRightsSecondLevelType).forEach(([key, value]) => {
    formValueObject.usageRightsGeneralInformation.buyoutRightsFormArray[key].subLevelType = value;
  });
  delete formValueObject.usageRightsGeneralInformation.buyoutRightsSecondLevelType;
}

export function checkStartDateAgainstEndDate(startField: FormControl<AnyType>, endField: FormControl<AnyType>, event: AnyType): void {
  if (endField.value && endField.value < event.value) {
    startField.setValue('');
    console.log('The start date can not be set after the end date.');
  }
}

export function checkEndDateAgainstStartDate(startField: FormControl<AnyType>, endField: FormControl<AnyType>, event: AnyType): void {
  if (startField.value && startField.value > event.value) {
    endField.setValue('');
    console.log('The end date can not be set before the start date.');
  }
}

export function updateAgencyOptions(fields: FormControlModel[], agencies: UserAgencyModel[]) {
  const agencyField = fields?.find((field: AnyType) => field.formFieldName === 'agency');
  if (agencyField) {
    agencyField.options = agencies;
    removeMultipleValueOption(agencyField);
  }
}

export function removeMultipleValueOption(formField: FormControlModel) {
  if (formField.options && formField.options?.length > 0) {
    const filterOptions = formField.options.filter((option) => option._id !== MULTI_VALUE && option.id !== MULTI_VALUE);
    formField.options = filterOptions;
  }
}
